import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from '../components/Toast';
import { useAuthContext } from '../context/AuthContext';
import { ENDPOINTS, canonicalUrl } from '../config';
import {
    formatPhoneWithPrefix,
    isValidKenyanPhone,
    normalizeKenyanPhone,
    KENYA_PHONE_ERROR,
    KENYA_PHONE_HINT,
    KENYA_PHONE_PLACEHOLDER,
    KENYA_PHONE_PREFIX,
} from '../utils/phone';

// Shown until the SEO Manager fetch resolves (and if it's ever unreachable) —
// SEO Manager (/admin/seo-manager, page "/register/") is the actual source
// of truth once a record exists there; this is only the pre-fetch/offline fallback.
const SEO_FALLBACK = {
    metaTitle: 'Create Account | FoneXpress Kenya',
    metaDescription:
        'Create a free FoneXpress Kenya account to track orders, save your wishlist, and check out faster.',
    robotsTag: 'noindex, nofollow',
};

export default function Register() {
    const navigate = useNavigate();
    const { register } = useAuthContext();

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: KENYA_PHONE_PREFIX,
        password: '',
        confirmPassword: '',
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [seo, setSeo] = useState(SEO_FALLBACK);

    useEffect(() => {
        fetch(ENDPOINTS.SEO.BY_URL('/register'))
            .then((res) => res.json())
            .then((data) => {
                const f = data?.isOk ? data.data : null;
                if (!f) return;
                setSeo({
                    metaTitle: f.metaTitle?.trim() || SEO_FALLBACK.metaTitle,
                    metaDescription: f.metaDescription?.trim() || SEO_FALLBACK.metaDescription,
                    robotsTag: f.robotsTag?.trim() || SEO_FALLBACK.robotsTag,
                });
            })
            .catch(() => {
                /* keep SEO_FALLBACK */
            });
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const next =
            name === 'phone' ? formatPhoneWithPrefix(value) : value;
        setFormData((prev) => ({ ...prev, [name]: next }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'Last name is required';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }
        if (!formData.phone.trim() || formData.phone.trim() === KENYA_PHONE_PREFIX) {
            newErrors.phone = 'Phone number is required';
        } else if (!isValidKenyanPhone(formData.phone)) {
            newErrors.phone = KENYA_PHONE_ERROR;
        }
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
        } else if (!/[A-Z]/.test(formData.password)) {
            newErrors.password = 'Password must contain at least one uppercase letter';
        } else if (!/[a-z]/.test(formData.password)) {
            newErrors.password = 'Password must contain at least one lowercase letter';
        } else if (!/\d/.test(formData.password)) {
            newErrors.password = 'Password must contain at least one digit';
        } else if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(formData.password)) {
            newErrors.password = 'Password must contain at least one special character';
        }
        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        try {
            const payload = {
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim(),
                phone: normalizeKenyanPhone(formData.phone),
                password: formData.password,
            };
            const result = await register(payload);
            if (result.isOk) {
                toast.success('Account created successfully');
                navigate('/', { replace: true });
            } else {
                toast.error(result.message || 'Registration failed. Please try again.');
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-muted flex items-center justify-center px-4 pt-[10px] pb-16">
            <Helmet>
                <title>{seo.metaTitle}</title>
                <meta name="description" content={seo.metaDescription} />
                <link rel="canonical" href={canonicalUrl('/register')} />
                <meta name="robots" content={seo.robotsTag} />
            </Helmet>
            <div className="w-full max-w-lg">
                <div className="bg-card rounded-xl shadow-sm border border-border p-8">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <h1 className="text-2xl font-bold text-foreground mb-2">Create Account</h1>
                        <p className="text-muted-foreground text-sm">Join FoneXpress for the best deals</p>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-5">
                        {/* Name Row */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="firstName" className="block text-sm font-medium text-foreground mb-1.5">
                                    First Name
                                </label>
                                <input
                                    id="firstName"
                                    name="firstName"
                                    type="text"
                                    autoComplete="given-name"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    placeholder="John"
                                    className={`w-full px-4 py-3 border rounded-lg text-sm outline-none transition-colors ${
                                        errors.firstName
                                            ? 'border-red-500 focus:border-red-500'
                                            : 'border-border focus:border-primary'
                                    }`}
                                />
                                {errors.firstName && (
                                    <p className="mt-1 text-xs text-red-500">{errors.firstName}</p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="lastName" className="block text-sm font-medium text-foreground mb-1.5">
                                    Last Name
                                </label>
                                <input
                                    id="lastName"
                                    name="lastName"
                                    type="text"
                                    autoComplete="family-name"
                                    value={formData.lastName}
                                    onChange={handleChange}
                                    placeholder="Doe"
                                    className={`w-full px-4 py-3 border rounded-lg text-sm outline-none transition-colors ${
                                        errors.lastName
                                            ? 'border-red-500 focus:border-red-500'
                                            : 'border-border focus:border-primary'
                                    }`}
                                />
                                {errors.lastName && (
                                    <p className="mt-1 text-xs text-red-500">{errors.lastName}</p>
                                )}
                            </div>
                        </div>

                        {/* Email */}
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">
                                Email Address
                            </label>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="you@example.com"
                                className={`w-full px-4 py-3 border rounded-lg text-sm outline-none transition-colors ${
                                    errors.email
                                        ? 'border-red-500 focus:border-red-500'
                                        : 'border-border focus:border-primary'
                                }`}
                            />
                            {errors.email && (
                                <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                            )}
                        </div>

                        {/* Phone */}
                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-1.5">
                                Phone Number
                            </label>
                            <input
                                id="phone"
                                name="phone"
                                type="tel"
                                autoComplete="tel"
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder={KENYA_PHONE_PLACEHOLDER}
                                className={`w-full px-4 py-3 border rounded-lg text-sm outline-none transition-colors ${
                                    errors.phone
                                        ? 'border-red-500 focus:border-red-500'
                                        : 'border-border focus:border-primary'
                                }`}
                            />
                            {errors.phone ? (
                                <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
                            ) : (
                                <p className="mt-1 text-xs text-muted-foreground">{KENYA_PHONE_HINT}</p>
                            )}
                        </div>

                        {/* Password */}
                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1.5">
                                Password
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="Min 8 characters"
                                    className={`w-full px-4 py-3 pr-12 border rounded-lg text-sm outline-none transition-colors ${
                                        errors.password
                                            ? 'border-red-500 focus:border-red-500'
                                            : 'border-border focus:border-primary'
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                    tabIndex={-1}
                                >
                                    <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                                </button>
                            </div>
                            {errors.password ? (
                                <p className="mt-1 text-xs text-red-500">{errors.password}</p>
                            ) : (
                                <p className="mt-1 text-xs text-muted-foreground">
                                    Must include uppercase, lowercase, digit, and special character
                                </p>
                            )}
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-1.5">
                                Confirm Password
                            </label>
                            <div className="relative">
                                <input
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    autoComplete="new-password"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    placeholder="Re-enter your password"
                                    className={`w-full px-4 py-3 pr-12 border rounded-lg text-sm outline-none transition-colors ${
                                        errors.confirmPassword
                                            ? 'border-red-500 focus:border-red-500'
                                            : 'border-border focus:border-primary'
                                    }`}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                    tabIndex={-1}
                                >
                                    <i className={showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                                </button>
                            </div>
                            {errors.confirmPassword && (
                                <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>
                            )}
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full py-3 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {submitting && (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            )}
                            {submitting ? 'Creating Account...' : 'Create Account'}
                        </button>
                    </form>

                    {/* Login Link */}
                    <p className="mt-6 text-center text-sm text-muted-foreground">
                        Already have an account?{' '}
                        <Link to="/login" className="text-primary font-medium hover:underline">
                            Sign In
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
